import { localToday } from '../../lib/flightFilters';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, getDocs, onSnapshot, query, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { StatusBanner } from '../../components/StatusBanner';
import { TRACKED_REG } from '../../lib/flightTrack';
import { isStudentPilot } from '../../lib/eligibility';
import {
  CHECKOUT_FLIGHT_ITEMS,
  CHECKOUT_GROUND_ITEMS,
  CHECKOUT_IFR_ITEMS,
  CHECKOUT_NIGHT_ITEMS,
  type CheckoutDoc,
  type UserDoc,
} from '../../lib/types';

/**
 * CFI checkout sign-off — the instructor's half of the two-step renter approval.
 *
 * The Policy's open-pilot clause covers "any Student or Renter pilot approved by the
 * Named Insured". This form is the evidence that approval rests on: a carrier-named
 * CFI certifies the checkout, the owner then approves the pilot from the admin side.
 * Only active users with billingProfile 'cfi' can reach this page or write the
 * collection (mirrored in firestore.rules) — today that is exactly the instructors
 * named on the policy's Approved Flight Instructor Amendment.
 */
export function CheckoutSignoff() {
  const { fbUser, profile, demoMode } = useAuth();
  const nav = useNavigate();

  const isCfi = profile?.billingProfile === 'cfi' && profile?.status === 'active';
  // The owner may sign a checkout himself — §4.3(d) is AJM's rule, not the carrier's.
  // Tracked separately from isCfi so the record shows which of the two signed.
  const isOwner = profile?.role === 'admin';
  const canSignOff = isCfi || isOwner;

  const [pilots, setPilots] = useState<UserDoc[]>([]);
  const [myCheckouts, setMyCheckouts] = useState<CheckoutDoc[]>([]);
  const [pilotUid, setPilotUid] = useState('');
  const [date, setDate] = useState(() => localToday());
  const [hobbs, setHobbs] = useState('');
  const [dualHours, setDualHours] = useState('');
  const [ground, setGround] = useState<Record<string, boolean>>({});
  const [flight, setFlight] = useState<Record<string, boolean>>({});
  // Privilege add-ons — the club model: core checkout for everyone, night and IFR
  // sections only when this checkout actually covered and grants them.
  const [inclNight, setInclNight] = useState(false);
  const [inclIfr, setInclIfr] = useState(false);
  const [night, setNight] = useState<Record<string, boolean>>({});
  const [ifr, setIfr] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [certNumber, setCertNumber] = useState(profile?.pilot?.certificateNumber ?? '');
  const [signature, setSignature] = useState('');
  const [attest, setAttest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!db || !canSignOff || demoMode) return;
    // Reads /pilotDirectory, not /users. A CFI cannot read /users (self-or-admin),
    // so this query used to be denied and the .then() never ran — leaving the picker
    // empty with nothing on screen to say why. The directory carries names only;
    // the trigger that maintains it already excludes owner and other CFIs.
    getDocs(collection(db, 'pilotDirectory'))
      .then((s) => {
        const list = s.docs
          .map((d) => {
            const x = d.data() as { firstName?: string; lastName?: string; studentPilot?: boolean };
            return {
              uid: d.id,
              personal: { firstName: x.firstName, lastName: x.lastName },
              ...(x.studentPilot ? { pilot: { certificateType: 'Student Pilot' } } : {}),
            } as UserDoc;
          })
          .sort((a, b) =>
            `${a.personal?.firstName ?? ''} ${a.personal?.lastName ?? ''}`.localeCompare(
              `${b.personal?.firstName ?? ''} ${b.personal?.lastName ?? ''}`
            )
          );
        setPilots(list);
        if (list.length === 0) setError('No pilots available to sign off. Ask Casey to check your CFI access.');
      })
      .catch((e) => {
        // A denied read used to fail silently. Say so.
        console.error('pilotDirectory', e);
        setError("Couldn't load the pilot list. Ask Casey to check your CFI access.");
      });
    const unsub = onSnapshot(
      query(collection(db, 'checkouts'), where('cfiUid', '==', fbUser?.uid ?? '')),
      (s) => setMyCheckouts(s.docs.map((d) => ({ ...(d.data() as CheckoutDoc), checkoutId: d.id })))
    );
    return unsub;
  }, [canSignOff, demoMode, fbUser?.uid]);

  const cfiName = `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim();
  const groundKeys = Object.keys(CHECKOUT_GROUND_ITEMS);
  const flightKeys = Object.keys(CHECKOUT_FLIGHT_ITEMS);
  const nightKeys = Object.keys(CHECKOUT_NIGHT_ITEMS);
  const ifrKeys = Object.keys(CHECKOUT_IFR_ITEMS);
  const allChecked =
    groundKeys.every((k) => ground[k]) &&
    flightKeys.every((k) => flight[k]) &&
    (!inclNight || nightKeys.every((k) => night[k])) &&
    (!inclIfr || ifrKeys.every((k) => ifr[k]));
  const signatureMatches = signature.trim().toLowerCase() === cfiName.toLowerCase();

  const selectedPilot = useMemo(
    () => pilots.find((p) => p.uid === pilotUid),
    [pilots, pilotUid]
  );

  // Casey 10/15/2026: the owner may still sign a PRIVATE-and-up renter's checkout, but never a
  // student's. A student flies only under a PB222-named CFI (Old Republic SPR). Mirrored in firestore.rules.
  const ownerBlockedForStudent = isOwner && !isCfi && isStudentPilot(selectedPilot?.pilot);

  const canSubmit =
    !ownerBlockedForStudent &&
    Boolean(pilotUid) &&
    Boolean(date) &&
    Number(dualHours) > 0 &&
    allChecked &&
    Boolean(certNumber.trim()) &&
    signatureMatches &&
    attest &&
    !submitting;

  if (!canSignOff) {
    return (
      <StatusBanner tone="warning" title="Instructors only">
        This page is for flight instructors named and approved under AJM&rsquo;s insurance policy.
      </StatusBanner>
    );
  }

  if (done) {
    return (
      <>
        <PageHeader title="Checkout sign-off" subtitle={`${TRACKED_REG} · 1961 Cessna 172C`} />
        <StatusBanner tone="success" title="Sign-off submitted">
          Thanks — AJM has been notified. The sign-off will be reviewed and the renter-pilot
          approval recorded; the pilot isn&rsquo;t cleared for solo rental until that happens.
        </StatusBanner>
        <button type="button" className="btn-secondary mt-4 text-sm" onClick={() => nav('/')}>
          Back to dashboard
        </button>
      </>
    );
  }

  const submit = async () => {
    if (!db || !fbUser || !canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const payload: Omit<CheckoutDoc, 'checkoutId' | 'signedAt'> = {
        aircraftId: TRACKED_REG,
        pilotUid,
        pilotName: `${selectedPilot?.personal?.firstName ?? ''} ${selectedPilot?.personal?.lastName ?? ''}`.trim(),
        cfiUid: fbUser.uid,
        cfiName,
        cfiCertNumber: certNumber.trim(),
        signedByRole: isOwner && !isCfi ? 'owner' : 'cfi',
        date,
        ...(hobbs ? { hobbs: Number(hobbs) } : {}),
        dualHoursGiven: Number(dualHours),
        pilotCertificate: [selectedPilot?.pilot?.certificateType, ...(selectedPilot?.pilot?.ratings ?? [])]
          .filter(Boolean)
          .join(' · ') || 'Not on file',
        groundItems: groundKeys,
        flightItems: flightKeys,
        privileges: ['vfr_day', ...(inclNight ? (['night'] as const) : []), ...(inclIfr ? (['ifr'] as const) : [])],
        ...(inclNight ? { nightItems: nightKeys } : {}),
        ...(inclIfr ? { ifrItems: ifrKeys } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        signatureName: signature.trim(),
        status: 'submitted',
      };
      const ref = await addDoc(collection(db, 'checkouts'), {
        ...payload,
        signedAt: serverTimestamp(),
      });
      await import('firebase/firestore').then(({ updateDoc }) =>
        updateDoc(ref, { checkoutId: ref.id })
      );
      setDone(true);
    } catch (e) {
      console.error(e);
      setError('Could not submit the sign-off. Try again, or contact AJM.');
    } finally {
      setSubmitting(false);
    }
  };

  const alreadySigned = myCheckouts.find((c) => c.pilotUid === pilotUid);

  return (
    <>
      <PageHeader
        title="Checkout sign-off"
        subtitle={`Certify a renter's checkout in ${TRACKED_REG} — the owner then records the insurance approval.`}
      />

      <div className="card mt-4 space-y-5 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <label className="label">Pilot</label>
            <select className="input" value={pilotUid} onChange={(e) => setPilotUid(e.target.value)}>
              <option value="">Select a pilot…</option>
              {pilots.map((p) => (
                <option key={p.uid} value={p.uid}>
                  {p.personal?.firstName} {p.personal?.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Checkout date</label>
            <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="label">Dual hours given</label>
            <input
              type="number"
              step="0.1"
              min="0"
              className="input"
              value={dualHours}
              onChange={(e) => setDualHours(e.target.value)}
              placeholder="e.g. 1.5"
            />
          </div>
        </div>

        {ownerBlockedForStudent && (
          <StatusBanner tone="warning" title="A CFI has to sign this one">
            This pilot is a student. Their checkout has to be signed by an instructor named on PB222, not by AJM.
          </StatusBanner>
        )}

        {alreadySigned && (
          <StatusBanner tone="info" title="Heads up">
            You already submitted a sign-off for this pilot on {alreadySigned.date}
            {alreadySigned.status === 'approved' ? ' (approved)' : ' (awaiting AJM approval)'}. Submitting
            again files a new one.
          </StatusBanner>
        )}

        <div>
          <label className="label">Hobbs at completion (optional)</label>
          <input
            type="number"
            step="0.1"
            className="input sm:max-w-xs"
            value={hobbs}
            onChange={(e) => setHobbs(e.target.value)}
          />
        </div>

        <div>
          <h3 className="font-display text-sm font-semibold text-navy-900">Ground — covered and satisfactory</h3>
          <div className="mt-2 space-y-2">
            {groundKeys.map((k) => (
              <label key={k} className="flex items-start gap-2.5 text-sm text-ink">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(ground[k])}
                  onChange={(e) => setGround({ ...ground, [k]: e.target.checked })}
                />
                {CHECKOUT_GROUND_ITEMS[k]}
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display text-sm font-semibold text-navy-900">Flight — demonstrated to a safe standard</h3>
          <div className="mt-2 space-y-2">
            {flightKeys.map((k) => (
              <label key={k} className="flex items-start gap-2.5 text-sm text-ink">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(flight[k])}
                  onChange={(e) => setFlight({ ...flight, [k]: e.target.checked })}
                />
                {CHECKOUT_FLIGHT_ITEMS[k]}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h3 className="font-display text-sm font-semibold text-navy-900">This checkout also grants</h3>
          <p className="mt-1 text-xs text-ink-muted">
            {/* Day VFR alone IS a complete checkout. No instrument rating is needed to
                be checked out in {TRACKED_REG}; Night and IFR are add-ons, signed off only if
                you actually covered them. */}
            <strong className="font-semibold text-navy-900">Day VFR on its own is a complete checkout.</strong>{' '}
            The core items above apply to every pilot, demonstrated to the standard of the certificate
            they hold{selectedPilot?.pilot?.certificateType ? ` (on file: ${[selectedPilot.pilot.certificateType, ...(selectedPilot.pilot.ratings ?? [])].join(' · ')})` : ''}.
            Tick Night or IFR only if you covered those too — an instrument-rated pilot files IFR in
            {TRACKED_REG} only once the IFR section is signed.
          </p>
          <div className="mt-3 flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm font-medium text-navy-900">
              <input type="checkbox" checked disabled /> Day VFR (always)
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-navy-900">
              <input type="checkbox" checked={inclNight} onChange={(e) => setInclNight(e.target.checked)} /> Night
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-navy-900">
              <input type="checkbox" checked={inclIfr} onChange={(e) => setInclIfr(e.target.checked)} /> IFR
            </label>
          </div>
        </div>

        {inclNight && (
          <div>
            <h3 className="font-display text-sm font-semibold text-navy-900">Night — covered and satisfactory</h3>
            <div className="mt-2 space-y-2">
              {nightKeys.map((k) => (
                <label key={k} className="flex items-start gap-2.5 text-sm text-ink">
                  <input type="checkbox" className="mt-0.5" checked={Boolean(night[k])} onChange={(e) => setNight({ ...night, [k]: e.target.checked })} />
                  {CHECKOUT_NIGHT_ITEMS[k]}
                </label>
              ))}
            </div>
          </div>
        )}

        {inclIfr && (
          <div>
            <h3 className="font-display text-sm font-semibold text-navy-900">IFR — covered and satisfactory</h3>
            <div className="mt-2 space-y-2">
              {ifrKeys.map((k) => (
                <label key={k} className="flex items-start gap-2.5 text-sm text-ink">
                  <input type="checkbox" className="mt-0.5" checked={Boolean(ifr[k])} onChange={(e) => setIfr({ ...ifr, [k]: e.target.checked })} />
                  {CHECKOUT_IFR_ITEMS[k]}
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="label">Notes (optional)</label>
          <textarea
            className="input min-h-[70px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything AJM should know — limitations discussed, areas to watch, recommendations."
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-ink">
            {/* The CFI clause is a statement about holding an instructor certificate and
                being named on the policy. The owner signing his own checkout is not
                making that claim, and putting those words under his signature would
                be a false record — §4.3(d) is AJM's rule, so he doesn't need to. */}
            I certify that I personally provided the ground and flight instruction above to this
            pilot in {TRACKED_REG}, a 1961 Cessna 172C, that the maneuvers were demonstrated to the
            standard of the certificate they hold, and that in my judgment they are competent to
            act as pilot in command of this aircraft for the privileges indicated:
            Day VFR{inclNight ? ', Night' : ''}{inclIfr ? ', IFR' : ''}.{' '}
            {isOwner && !isCfi
              ? `I am the owner of ${TRACKED_REG} and am recording this checkout under AJM\u2019s own checkout requirement.`
              : 'I hold a current flight instructor certificate and I am named and approved under AJM\u2019s insurance policy.'}
          </p>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label">
                {isOwner && !isCfi ? 'Your certificate number' : 'CFI certificate number'}
              </label>
              <input className="input" value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
            </div>
            <div>
              <label className="label">Type your full name to sign</label>
              <input
                className="input"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={cfiName}
              />
              {signature && !signatureMatches && (
                <p className="mt-1 text-xs text-amber-700">Must match your name on file: {cfiName}</p>
              )}
            </div>
          </div>
          <label className="mt-3 flex items-start gap-2.5 text-sm text-ink">
            <input type="checkbox" className="mt-0.5" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
            Signing electronically — this sign-off is recorded with a server timestamp and sent to
            the owner.
          </label>
        </div>

        {error && <StatusBanner tone="warning" title="Something went wrong">{error}</StatusBanner>}

        <button type="button" className="btn-primary" disabled={!canSubmit} onClick={submit}>
          {submitting ? 'Submitting…' : 'Submit sign-off'}
        </button>
        <p className="text-xs text-ink-muted">
          Submitting does not clear the pilot to rent. AJM reviews this and records the
          renter-pilot approval required by the insurance policy; the pilot is notified by AJM.
        </p>
      </div>
    </>
  );
}
